export function parseTime(timeStr) {
	// Assumes timeStr is in "HH:MM" format if it's a non-empty string.
	// Robust validation (e.g., for NaN on parseInt) could be added if inputs are uncertain.
	const parts = timeStr.split(":");
	const hours = parseInt(parts[0], 10);
	const minutes = parseInt(parts[1], 10);

	return hours * 60 + minutes;
}

export function formatTime(totalMinutes) {
	const hours = Math.floor(totalMinutes / 60) % 24; // Display hours in 00-23 range
	const minutes = totalMinutes % 60;

	return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export function generateSchedules({slot, restStartTime, restEndTime, startTime, endTime, quota, buffer}) {
	const schedules = [];
	const slotDuration = parseInt(slot, 10);
	const bufferDuration = parseInt(buffer, 10);

	const startTimeMinutesOriginal = parseTime(startTime);
	let currentTimeInMinutes = startTimeMinutesOriginal;

	const endTimeMinutesOriginal = parseTime(endTime);
	let overallEndTimeInMinutes = endTimeMinutesOriginal;

	// Adjust overallEndTimeInMinutes if the schedule period crosses midnight
	if (endTimeMinutesOriginal < startTimeMinutesOriginal) {
		overallEndTimeInMinutes += 24 * 60;
	}
	// Handle case where overall end time might be exactly 24 hours later like 08:00 to 08:00 next day
	// If startTime and endTime are identical, it might mean a 24-hour cycle or zero duration.
	// The current logic implies zero duration if they are same and not adjusted by above.
	// If overallEndTimeInMinutes is 00:00 and it's meant as end of day, it becomes 24*60 if startTime was 00:00.
	else if (
		endTimeMinutesOriginal === startTimeMinutesOriginal &&
		endTimeMinutesOriginal === 0 &&
		startTimeMinutesOriginal === 0 &&
		endTime === "00:00"
	) {
		// Special case for "00:00" to "00:00" potentially meaning a full 24-hour day
		// For this to mean 24h, endTime would effectively be 24:00
		// But if it implies 0 duration, the loop `while(currentTime < 0)` handles it.
		// The previous `overallEndTimeInMinutes += 24 * 60` if `endTimeMinutesOriginal < startTimeMinutesOriginal` is the primary overnight handler.
		// If `startTime: "00:00", endTime: "00:00"`, then `overallEndTimeInMinutes` remains `0`. The loop `0 < 0` is false. `[]` is returned.
		// This is reasonable for zero duration. If 24h is intended, endTime might be "23:59" or specific logic added.
	}

	let restStartM = -1,
		restEndM = -1;
	const hasRestPeriod = restStartTime && restStartTime.trim() !== "" && restEndTime && restEndTime.trim() !== "";

	if (hasRestPeriod) {
		restStartM = parseTime(restStartTime);
		let parsedRestEndTime = parseTime(restEndTime);

		restEndM = parsedRestEndTime;

		// Adjust restEndM if the rest period itself crosses midnight
		if (parsedRestEndTime < restStartM) {
			restEndM += 24 * 60;
		}
	}

	while (currentTimeInMinutes < overallEndTimeInMinutes) {
		// 1. Check if currentTimeInMinutes is currently inside a defined rest period
		if (hasRestPeriod && currentTimeInMinutes >= restStartM && currentTimeInMinutes < restEndM) {
			currentTimeInMinutes = restEndM; // Jump to the end of the rest period
			continue; // Restart loop to re-evaluate conditions with the new currentTimeInMinutes
		}

		const slotEndTimeInMinutes = currentTimeInMinutes + slotDuration;

		// 2. Check if the proposed slot would end after the overallEndTimeInMinutes
		if (slotEndTimeInMinutes > overallEndTimeInMinutes) {
			break; // Slot doesn't fit within the main schedule
		}

		// 3. Check if the proposed slot [currentTimeInMinutes, slotEndTimeInMinutes] overlaps with the rest period
		let overlapsWithRest = false;

		if (hasRestPeriod) {
			// A slot [sS, sE] overlaps with rest [rS, rE] if sS < rE AND sE > rS
			if (currentTimeInMinutes < restEndM && slotEndTimeInMinutes > restStartM) {
				overlapsWithRest = true;
			}
		}

		if (overlapsWithRest) {
			// If it overlaps, we cannot place this slot here.
			// Advance currentTimeInMinutes to the end of the rest period and try again.
			currentTimeInMinutes = restEndM;
			continue;
		}

		// If no overlap and within bounds, add the schedule
		schedules.push({
			startTime: formatTime(currentTimeInMinutes),
			endTime: formatTime(slotEndTimeInMinutes),
			quota,
		});

		// Advance time for the next slot: to the end of the current slot + buffer
		currentTimeInMinutes = slotEndTimeInMinutes;
		if (bufferDuration > 0) {
			currentTimeInMinutes += bufferDuration;
		}
	}

	return schedules;
}
